// biome-ignore lint/style/useImportType: keeping value import for compatibility
import * as React from 'react';
import { type FC, Fragment, useEffect } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useMe } from '@crehana/auth';
import { FeatureFlagProvider } from '@crehana/feature-flags/react';
import { useTranslation } from '@crehana/i18n';
import {
  CrehanaNextSeo,
  getG11nAndTranslationsFromServer,
  initializeApollo,
} from '@crehana/next';
import type { GetServerSideProps } from 'next/types';
// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { OpenGraph } from 'next-seo/lib/types';

import { WrapperMainFooter } from '@/shared/components/wrapper-main-footer';
import usePersistQueryParams from '@/shared/hooks/usePersistQueryParams';
import { Header } from '@/views/B2BLanding/components';

import { AMPLITUDE_TRACKS } from '../../events';
import localeViewEn from '../../i18n/localeEn';
import localeViewEs from '../../i18n/localeEs';
import localeViewPt from '../../i18n/localePt';
import GET_DOWNLOADABLE_RESOURCE_DETAIL from './graphql/GetDownloadableResourceDetail.graphql';
// biome-ignore lint/style/useImportType: legacy import, will be migrated
import {
  GetDownloadableResourceDetail,
  GetDownloadableResourceDetailVariables,
} from './graphql/types/GetDownloadableResourceDetail';
import localeEn from './i18n/localeEn';
import localeEs from './i18n/localeEs';
import localePt from './i18n/localePt';
import Body from './sections/Body';
import Recommendations from './sections/Recommendations';

interface DownloadableDetailProps {
  SEO?: OpenGraph;
  originalId: number;
}

const PAGE_KEY = 'b2b-landing-downloadable-detail';

const DownloadableDetail: FC<
  React.PropsWithChildren<DownloadableDetailProps>
> = ({ SEO, originalId }) => {
  usePersistQueryParams();
  const { addResourceBundle } = useTranslation();
  const { logEvent } = useAmplitude();
  const { me } = useMe();
  const organizationOriginalId = me?.organization?.originalId;

  addResourceBundle(
    {
      es: { [PAGE_KEY]: { ...localeEs.es, ...localeViewEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt, ...localeViewPt.pt } },
      en: { [PAGE_KEY]: { ...localeEn.en, ...localeViewEn.en } },
    },
    { injectOnce: true },
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    logEvent({
      event: AMPLITUDE_TRACKS.LANDING_PAGE_VIEWED,
      data: {
        ...getSourceEventParams(),
        Product: `B2B Downloadables Detail ${SEO?.title}`,
        pageSlug: originalId,
        LandingName: 'Downloadables',
      },
    });
  }, []);

  return (
    <Fragment>
      <FeatureFlagProvider
        organizationId={organizationOriginalId!}
        flags={[['LEARNING_HIDE_BANNER_DOWNLOAD_APP', 'V5']]}
      >
        <CrehanaNextSeo
          hasCountryCode={false}
          title={SEO?.title}
          description={SEO?.description}
          openGraph={SEO}
        />
        <Header />
        <Body />
        <Recommendations />
        <WrapperMainFooter />
      </FeatureFlagProvider>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  const apolloClient = initializeApollo({
    headers: req.headers,
  });
  const slug = Array.isArray(params!.slug) ? params!.slug[0] : params!.slug;

  const { data: resourceData } = await apolloClient.query<
    GetDownloadableResourceDetail,
    GetDownloadableResourceDetailVariables
  >({
    query: GET_DOWNLOADABLE_RESOURCE_DETAIL,
    variables: {
      slug,
    },
  });

  if (!resourceData.resource) {
    return {
      notFound: true,
    };
  }

  const SEO: OpenGraph = {
    title: resourceData.resource.seoTitle || resourceData.resource.title,
    description:
      resourceData.resource.seoDescription || resourceData.resource.description,
    images: [
      {
        url:
          resourceData.resource.coverUrl ||
          resourceData.resource.cardImageUrl ||
          '',
      },
    ],
  };

  const g11Data = await getG11nAndTranslationsFromServer({
    headers: req.headers,
    pageKey: PAGE_KEY,
    defaultTranslations: {
      es: { [PAGE_KEY]: { ...localeEs.es, ...localeViewEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt, ...localeViewPt.pt } },
      en: { [PAGE_KEY]: { ...localeEn.en, ...localeViewEn.en } },
    },
  });

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      SEO,
      slug,
      originalId: resourceData.resource.originalId,
      ...g11Data,
    },
  };
};

export default DownloadableDetail;
